'use client';

import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Upload, Download, Copy, Trash2, ArrowRight, ArrowDown, Code } from 'lucide-react';
import { saveAs } from 'file-saver';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { xmlFormatterContent } from '../../data/xml-formatter-content';
import Link from 'next/link';

interface CodeEditorProps {
    value: string;
    onChange?: (value: string) => void;
    readOnly?: boolean;
    placeholder?: string;
    className?: string;
}

const CodeEditor = ({ value, onChange, readOnly, placeholder, className }: CodeEditorProps) => {
    return (
        <div className={cn(
            "relative flex h-full w-full overflow-hidden rounded-md border bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            className
        )}>
            <Textarea
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                readOnly={readOnly}
                className="h-full w-full resize-none border-0 focus-visible:ring-0 rounded-none p-4 font-mono text-sm leading-6"
                placeholder={placeholder}
                spellCheck={false}
            />
        </div>
    );
};

const formatXml = (xml: string, indentSize: number = 2): { formatted: string; error?: string } => {
    try {
        // Remove all line breaks and extra spaces
        const trimmed = xml.replace(/\s+/g, ' ').trim();
        if (!trimmed) return { formatted: '', error: 'Empty XML content' };

        let formatted = '';
        let indent = '';
        const tab = ' '.repeat(indentSize);
        let inTag = false;
        let inAttribute = false;
        let currentTag = '';
        let i = 0;

        // Check if it's a complete XML document
        if (!trimmed.startsWith('<') || !trimmed.includes('>')) {
            return { formatted: xml, error: 'Invalid XML format' };
        }

        // Format the XML with proper indentation
        while (i < trimmed.length) {
            const char = trimmed[i];
            const nextChar = trimmed[i + 1];

            if (char === '<') {
                if (nextChar === '/') {
                    // Closing tag
                    if (inTag) {
                        formatted += '>';
                        inTag = false;
                    }
                    formatted += '\n' + indent.slice(0, -indentSize) + char;
                } else if (nextChar === '?' || nextChar === '!') {
                    // XML declaration or comment
                    if (inTag) {
                        formatted += '>';
                        inTag = false;
                    }
                    formatted += '\n' + indent + char;
                } else {
                    // Opening tag
                    if (inTag) {
                        formatted += '>';
                    }
                    formatted += '\n' + indent + char;
                    inTag = true;
                }
            } else if (char === '>') {
                formatted += char;
                inTag = false;
                inAttribute = false;
                currentTag = '';
            } else if (char === ' ' && inTag && !inAttribute) {
                // Start of attributes
                inAttribute = true;
                formatted += ' ';
            } else if (char === '"' && inTag && inAttribute) {
                // Toggle attribute value
                inAttribute = !inAttribute;
                formatted += char;
            } else if (char === '/' && nextChar === '>' && inTag) {
                // Self-closing tag
                formatted += ' ' + char + nextChar;
                i++; // Skip the '>'
                inTag = false;
            } else {
                formatted += char;
                if (inTag && !inAttribute && char !== ' ') {
                    currentTag += char;
                }
            }

            // Handle indentation for nested tags
            if (char === '>' && nextChar === '<' && trimmed[i + 2] !== '/') {
                indent += tab;
            } else if (char === '>' && nextChar === '<' && trimmed[i + 2] === '/') {
                indent = indent.slice(0, -indentSize);
            }

            i++;
        }

        // Clean up any remaining formatting issues
        formatted = formatted.replace(/\n\s*\n/g, '\n').trim();

        return { formatted };
    } catch (error) {
        console.error('Error formatting XML:', error);
        return {
            formatted: xml,
            error: error instanceof Error ? error.message : 'An error occurred while formatting XML'
        };
    }
};

const validateXml = (xml: string): { valid: boolean; error?: string } => {
    try {
        // Check for basic XML structure
        if (!xml.trim().startsWith('<')) {
            return { valid: false, error: 'Invalid XML: Must start with <' };
        }

        // Check for matching tags
        const tagStack: string[] = [];
        const tagRegex = /<(\/?)([^>\s]+)[^>]*>/g;
        let match;
        let lastIndex = 0;

        while ((match = tagRegex.exec(xml)) !== null) {
            const [fullMatch, isClosing, tagName] = match;
            const tag = tagName.split(' ')[0]; // Get just the tag name without attributes

            if (tag.startsWith('!') || tag.startsWith('?')) {
                // Skip XML declaration, comments, and other special tags
                continue;
            }

            if (isClosing) {
                if (tagStack.length === 0 || tagStack[tagStack.length - 1] !== tag) {
                    return {
                        valid: false,
                        error: `Mismatched tags: Expected </${tagStack[tagStack.length - 1]}>, found </${tag}>`
                    };
                }
                tagStack.pop();
            } else if (!fullMatch.endsWith('/>')) {
                // Only push to stack if it's not a self-closing tag
                tagStack.push(tag);
            }

            lastIndex = tagRegex.lastIndex;
        }

        // Check for unclosed tags
        if (tagStack.length > 0) {
            return {
                valid: false,
                error: `Unclosed tag: <${tagStack[tagStack.length - 1]}>`
            };
        }

        // Check for text outside root element
        const textAfterRoot = xml.slice(lastIndex).trim();
        if (textAfterRoot) {
            return {
                valid: false,
                error: 'Text content found outside root element'
            };
        }

        return { valid: true };
    } catch (error) {
        return {
            valid: false,
            error: error instanceof Error ? error.message : 'An error occurred while validating XML'
        };
    }
};

export default function XmlPrettifier() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [indentSize, setIndentSize] = useState(2);
    const [preserveAttributes, setPreserveAttributes] = useState(true);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    const formatXmlHandler = () => {
        try {
            if (!input.trim()) {
                toast({
                    title: "Error",
                    description: "Please enter some XML to format",
                    variant: "destructive",
                });
                return;
            }

            // Validate XML
            const validation = validateXml(input);
            if (!validation.valid) {
                toast({
                    title: "XML Validation Error",
                    description: validation.error || "Invalid XML format",
                    variant: "destructive",
                });
                return;
            }

            const { formatted, error } = formatXml(input, indentSize);

            if (error) {
                throw new Error(error);
            }

            setOutput(formatted);

            toast({
                title: "Success",
                description: "XML formatted successfully!",
            });
        } catch (error) {
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "An error occurred while formatting XML",
                variant: "destructive",
            });
            console.error('Error formatting XML:', error);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
            toast({
                title: "File Uploaded",
                description: `Loaded ${file.name}`,
            });
        };
        reader.readAsText(file);
    };

    const handleDownload = () => {
        if (!output) return;
        const blob = new Blob([output], { type: "application/xml;charset=utf-8" });
        saveAs(blob, "formatted.xml");
        toast({
            title: "Downloaded",
            description: "File saved as formatted.xml",
        });
    };

    const copyToClipboard = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        toast({
            title: "Copied!",
            description: "Formatted XML copied to clipboard",
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const sampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<catalog>
  <book id="bk101">
    <author>Gambardella, Matthew</author>
    <title>XML Developer's Guide</title>
    <genre>Computer</genre>
    <price>44.95</price>
    <publish_date>2000-10-01</publish_date>
    <description>An in-depth look at creating applications with XML.</description>
  </book>
  <book id="bk102">
    <author>Ralls, Kim</author>
    <title>Midnight Rain</title>
    <genre>Fantasy</genre>
    <price>5.95</price>
    <publish_date>2000-12-16</publish_date>
    <description>A former architect battles corporate zombies, an evil sorceress, and her own childhood to become queen of the world.</description>
  </book>
  <book id="bk103">
    <author>Corets, Eva</author>
    <title>Maeve Ascendant</title>
    <genre>Fantasy</genre>
    <price>5.95</price>
    <publish_date>2000-11-17</publish_date>
    <description>After the collapse of a nanotechnology society in England, the young survivors lay the foundation for a new society.</description>
  </book>
</catalog>`;

    const loadSample = () => {
        setInput(sampleXml);
    };

    return (
        <div className="container mx-auto p-4 max-w-[1400px]">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <ToolPageIntro title="XML Formatter">
                Format and beautify XML code with our free online tool. Clean up messy XML files and improve code readability with proper indentation.
            </ToolPageIntro>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={loadSample}>
                        Load Sample
                    </Button>
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                        <Upload className="mr-2 h-4 w-4" /> Upload File
                    </Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept=".xml"
                        onChange={handleFileUpload}
                    />
                </div>
            </div>

            <div className="mb-6 p-4 border rounded-lg bg-muted/30">
                <h3 className="font-medium mb-3">Formatting Options</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center justify-between">
                        <label htmlFor="indent-size" className="text-sm">Indent Size</label>
                        <input
                            id="indent-size"
                            type="number"
                            min="1"
                            max="8"
                            value={indentSize}
                            onChange={(e) => setIndentSize(parseInt(e.target.value) || 2)}
                            className="w-20 px-2 py-1 border rounded text-sm"
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <label htmlFor="preserve-attributes" className="text-sm">Preserve Attributes</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                id="preserve-attributes"
                                checked={preserveAttributes}
                                onChange={(e) => setPreserveAttributes(e.target.checked)}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 items-stretch">
                {/* Input Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-[700px]">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg font-medium flex justify-between items-center">
                            <span>Input XML</span>
                            <Button variant="ghost" size="sm" onClick={clearAll} className="text-muted-foreground hover:text-destructive">
                                <Trash2 className="h-4 w-4 mr-1" /> Clear
                            </Button>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 relative overflow-hidden">
                        <CodeEditor
                            value={input}
                            onChange={setInput}
                            placeholder="Paste your XML here, upload a file, or try the sample..."
                            className="border-0 rounded-none"
                        />
                    </CardContent>
                </Card>

                {/* Action Section */}
                <div className="flex lg:flex-col justify-center items-center gap-4 py-4 lg:py-0">
                    <Button
                        onClick={formatXmlHandler}
                        size="lg"
                        className="rounded-full w-16 h-16 lg:w-20 lg:h-20 p-0 shadow-lg hover:scale-105 transition-transform"
                        title="Format XML"
                    >
                        <ArrowRight className="h-8 w-8 hidden lg:block" />
                        <ArrowDown className="h-8 w-8 lg:hidden" />
                    </Button>
                </div>

                {/* Output Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-[700px]">
                    <CardHeader className="pb-3 border-b bg-muted/20">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <CardTitle className="text-lg font-medium">Formatted Output</CardTitle>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="icon" onClick={copyToClipboard} disabled={!output} title="Copy">
                                    <Copy className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="icon" onClick={handleDownload} disabled={!output} title="Download">
                                    <Download className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 bg-muted/30 relative overflow-hidden">
                        <CodeEditor
                            value={output}
                            readOnly
                            placeholder="Formatted XML will appear here..."
                            className="border-0 rounded-none bg-transparent"
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={xmlFormatterContent.title}
                description={xmlFormatterContent.description}
                content={xmlFormatterContent.content}
                faqs={xmlFormatterContent.faqs}
                relatedTools={xmlFormatterContent.relatedTools}
            />

            <Toaster />
        </div>
    );
}
